/**
 * SandboxRuntime — the primary runtime for code execution.
 *
 * Communicates with the server's SandboxModule via REST + WebSocket
 * to manage a K8s pod for code execution.
 */

import { atom } from 'nanostores';
import { io, type Socket } from 'socket.io-client';
import { apiGetSandboxTree } from '~/lib/api/sandbox-files';
import { apiRefreshToken } from '~/lib/api/auth';
import { captureRequestError } from '~/lib/posthog';
import { SandboxFileSystem } from './sandbox-filesystem';
import { SandboxProcess } from './sandbox-process';
import { WORK_DIR } from '~/utils/constants';
import { $accessToken, $currentUser } from '~/lib/stores/auth';
import { setTimeoutWarning, setTimedOut, clearTimeoutState } from '~/lib/stores/connection-status';
import { applyQueueUpdate, admitFromQueue } from '~/lib/stores/turn-queue';

/**
 * Sandbox lifecycle status.
 *
 * - 'idle'     — nothing started
 * - 'booting'  — creating the pod + connecting the WebSocket
 * - 'starting' — socket joined, but the dev server is NOT confirmed serving yet.
 *                This is the honest state between "socket connected" and "preview
 *                actually serves". A fresh chat sits here until the agent boots the
 *                dev server; a resumed pod sits here until a preview-url probe / a
 *                :5173 port event confirms it. (DEV-155 — removes the false-ready
 *                window where 'running' was declared the instant the socket joined.)
 * - 'running'  — the dev server is actually serving a preview.
 * - 'sleeping' — pod garbage-collected / session timed out; wake to resume.
 * - 'error'    — terminal boot/readiness failure; the Retry banner renders on this.
 */
export type SandboxStatus = 'idle' | 'booting' | 'starting' | 'running' | 'sleeping' | 'error';
export type ServerReadyCallback = (port: number, url: string) => void;
export type PortCallback = (port: number, type: 'open' | 'close', url: string) => void;
export type PreviewMessageCallback = (message: any) => void;

export class SandboxRuntime {
  readonly workdir = WORK_DIR;
  readonly fs: SandboxFileSystem;
  readonly status = atom<SandboxStatus>('idle');
  readonly hasStaticPreview = atom<boolean>(false);

  private _getAuthHeaders(): HeadersInit {
    const token = $accessToken.get();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * `fetch` for the sandbox REST endpoints, with the same 401 handling the
   * axios client gets from its interceptor: refresh the session once and retry.
   *
   * These calls read the cached `$accessToken` directly, so a tab left open
   * past the token's lifetime used to POST /sandbox/create with a dead token,
   * get "Unauthorized", and land the user on an error that a refresh would have
   * avoided. `apiRefreshToken` is shared with the interceptor (it coalesces
   * in-flight refreshes), so this can't rotate the token twice. If the refresh
   * itself fails the original 401 is returned for the caller to surface.
   */
  private async _authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
    const send = () => fetch(url, { credentials: 'include', ...init, headers: this._getAuthHeaders() });
    const response = await send();

    if (response.status !== 401) {
      return response;
    }

    try {
      const { accessToken } = await apiRefreshToken();
      $accessToken.set(accessToken);
    } catch {
      return response;
    }

    return send();
  }

  /**
   * Run boot() from a callback nobody awaits (socket event, visibility
   * listener). A failed boot already lands in 'error' — the Retry banner — but
   * the rejection itself went unhandled and surfaced in PostHog as a bare
   * "Unauthorized" / "WebSocket connection timeout" with no context.
   */
  private _bootInBackground(chatId: string, context: string): void {
    this.boot(chatId).catch((err) => {
      // _boot sets 'error' on every failure path it owns; this covers anything
      // that rejected before reaching it, but never stomps a switched chat.
      if (this._chatId === chatId && this.status.get() === 'booting') {
        this.status.set('error');
      }

      captureRequestError(err, { context, chatId });
    });
  }

  /**
   * Shim for `sandbox.internal.watchPaths()` — called by FilesStore.
   * Translates to SandboxFileSystem.watch() with event type mapping.
   */
  readonly internal = {
    /**
     * Text search across project files via the sandbox pod.
     * Falls back to searching the in-memory FileMap if pod is unavailable.
     */
    textSearch: async (
      query: string,
      _options?: { include?: string; exclude?: string },
      onProgress?: (results: any[]) => void,
    ) => {
      if (!this._chatId) {
        return;
      }

      try {
        const res = await this._authedFetch(`/api/sandbox/${this._chatId}/files/list?path=/home/project`);

        if (!res.ok) {
          return;
        }

        /*
         * For now, search is handled client-side via the FileMap
         * A proper server-side grep could be added later
         */
        onProgress?.([]);
      } catch {
        // Search unavailable
      }
    },

    watchPaths: (
      options: { include?: string[]; exclude?: string[]; includeContent?: boolean },
      callback: (events: Array<{ type: string; path: string; buffer?: Uint8Array }>) => void,
    ) => {
      this.fs.watch(options, (events) => {
        // Map FileWatchEvent types to PathWatcherEvent types
        const mapped = events.map((e) => {
          let type: string;

          switch (e.type) {
            case 'file:change':
              type = e.content !== undefined ? 'change' : 'add_file';
              break;
            case 'file:unlink':
              type = 'remove_file';
              break;
            case 'dir:add':
              type = 'add_dir';
              break;
            // Seeds from the workspace listing: a path with no bytes behind it
            // yet. Kept distinct from add_file/add_dir so the store can tell
            // "we know this exists" from "here is its content".
            case 'tree:seed_dir':
              type = 'seed_dir';
              break;
            case 'tree:seed_file':
              type = 'seed_file';
              break;
            default:
              type = 'change';
          }

          return {
            type,
            path: e.path,
            buffer: e.content != null ? new TextEncoder().encode(e.content) : undefined,
          };
        });

        callback(mapped);
      });
    },
  };

  private _socket: Socket | null = null;
  private _chatId: string | null = null;
  private _booted = false;
  private _bootInFlight: Promise<void> | null = null;
  private _bootInFlightChatId: string | null = null;
  private _serverReadyCallbacks: ServerReadyCallback[] = [];
  private _portCallbacks: PortCallback[] = [];
  private _previewMessageCallbacks: PreviewMessageCallback[] = [];
  private _knownPorts = new Set<number>();

  /** Chat whose pod-not-found reboot is parked until the tab is looked at again. */
  private _deferredRebootChatId: string | null = null;

  /** The parked reboot's visibilitychange listener, kept so teardown can detach it. */
  private _deferredRebootListener: (() => void) | null = null;

  constructor() {
    this.fs = new SandboxFileSystem(() => this._chatId);
  }

  /**
   * Check if a sandbox pod exists for this chat.
   * Sets status to 'sleeping' if the pod was garbage collected.
   *
   * NOTE: a live pod (200) does NOT imply its dev server is serving — that's the
   * DEV-155 distinction. So this only *returns* 'running' for the caller's
   * branching; it deliberately does not *set* status to 'running' (that would
   * re-open the false-ready window). The transition to 'running' happens only
   * once serving is confirmed (boot's serving-probe / a :5173 port event).
   */
  async checkStatus(chatId: string): Promise<SandboxStatus> {
    try {
      const response = await this._authedFetch(`/api/sandbox/${chatId}/status`);

      if (response.ok) {
        return 'running';
      }

      // 404 = pod doesn't exist (was GC'd or never created)
      this.status.set('sleeping');

      return 'sleeping';
    } catch {
      this.status.set('sleeping');

      return 'sleeping';
    }
  }

  async boot(chatId: string, projectType?: string): Promise<void> {
    const currentStatus = this.status.get();

    // Already connected for this chat with a live socket — nothing to do.
    // 'starting' counts here too: the socket is joined and _booted is true, the
    // dev server just hasn't confirmed serving yet (DEV-155). Re-booting would
    // tear down the live socket and re-create the pod for no reason.
    if (
      this._booted &&
      this._chatId === chatId &&
      (currentStatus === 'running' || currentStatus === 'starting') &&
      this._socket?.connected
    ) {
      console.log('[Sandbox] Already booted, skipping');
      return;
    }

    /*
     * Coalesce concurrent boots for the same chat. Several triggers (chat load,
     * message send, preview reboot after streaming, resume) can call boot()
     * within the same tick. Without this guard each call would disconnect the
     * in-flight socket and POST /sandbox/create again — a reconnect/create storm
     * that claims (and burns) multiple warm pods. Share the one in-flight boot.
     */
    if (this._bootInFlight && this._bootInFlightChatId === chatId) {
      console.log('[Sandbox] boot() already in flight for this chat — reusing');
      await this._bootInFlight;

      return;
    }

    const inFlight = this._boot(chatId, projectType).finally(() => {
      // Only clear if we still own the slot — a newer boot (e.g. chat switch)
      // may have replaced it while this one was settling. Clearing
      // unconditionally would wipe the newer boot's tracking and let a
      // duplicate _boot() run, reintroducing the create/reconnect storm.
      if (this._bootInFlight === inFlight) {
        this._bootInFlight = null;
        this._bootInFlightChatId = null;
      }
    });

    this._bootInFlightChatId = chatId;
    this._bootInFlight = inFlight;

    await inFlight;
  }

  /**
   * Park a pod reboot until the tab is foregrounded.
   *
   * Idempotent: repeated pod-not-found events (the server emits to the whole
   * chat room, so every socket in it reacts) overwrite the parked chat rather
   * than stacking listeners. The listener re-checks the parked id at fire time,
   * so a chat switch while hidden can't resurrect the abandoned one.
   */
  private _deferReboot(chatId: string): void {
    const alreadyParked = this._deferredRebootChatId !== null;
    this._deferredRebootChatId = chatId;

    if (alreadyParked || typeof document === 'undefined') {
      return;
    }

    const onVisible = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const parked = this._deferredRebootChatId;
      this._clearDeferredReboot();

      // Only revive the chat the user is actually on now.
      if (parked && parked === this._chatId) {
        console.log(`[Sandbox] Tab visible again — recreating pod for chat ${parked}`);
        this._bootInBackground(parked, 'sandbox_reboot_on_visible');
      }
    };

    this._deferredRebootListener = onVisible;
    document.addEventListener('visibilitychange', onVisible);
  }

  /**
   * Drop a parked reboot and detach its listener.
   *
   * The two must move together: `_deferReboot` decides whether to attach by
   * testing `_deferredRebootChatId`, so a cleared id with a live listener (or
   * the reverse) means the next pod-not-found either stacks a second listener
   * or parks a reboot nothing will ever run.
   */
  private _clearDeferredReboot(): void {
    this._deferredRebootChatId = null;

    if (this._deferredRebootListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._deferredRebootListener);
    }

    this._deferredRebootListener = null;
  }

  private async _boot(chatId: string, projectType?: string): Promise<void> {
    console.log(`[Sandbox] boot() called — chatId=${chatId}, booted=${this._booted}, status=${this.status.get()}`);

    // Clean up previous connection if switching chats or re-booting
    if (this._socket) {
      this._socket.disconnect();
      this._socket = null;
    }

    this._booted = false;
    this._knownPorts.clear();

    /*
     * NOTE: do NOT clear _serverReadyCallbacks/_portCallbacks/_previewMessageCallbacks
     * — they are registered once by PreviewsStore.#init() and must persist across reboots
     */

    this._chatId = chatId;
    this.status.set('booting');

    // Create the sandbox pod via REST. A network failure here (fetch rejects)
    // must leave a terminal 'error' status, not a stuck 'booting' (DEV-146).
    let response: Response;

    try {
      response = await this._authedFetch('/api/sandbox/create', {
        method: 'POST',
        body: JSON.stringify({ chatId, projectType }),
      });
    } catch (err) {
      this.status.set('error');
      throw err;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create sandbox' }));
      this.status.set('error');
      throw new Error(error.error || error.message || 'Failed to create sandbox');
    }

    // Connect WebSocket with auth token
    const token = $accessToken.get();
    this._socket = io('/sandbox', {
      transports: ['websocket'],
      withCredentials: true,
      auth: { token: token || 'dev' },
    });

    // A WebSocket-stage failure (connect timeout / connect_error) used to reject
    // while leaving status stuck at 'booting' — no UI renders that, so the user
    // was stranded on a silently-broken edit page. Land in terminal 'error'
    // instead, so the retry affordance can show (DEV-146).
    try {
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('WebSocket connection timeout')), 10000);

        this._socket!.on('connect', () => {
          clearTimeout(timeout);

          // Join the chat room. Send userId so server-side sandbox events attribute
          // to the real person instead of spawning a phantom PostHog profile per chat.
          this._socket!.emit('join', { chatId, userId: $currentUser.get()?.id }, () => {
            resolve();
          });
        });

        this._socket!.on('connect_error', (err) => {
          clearTimeout(timeout);
          reject(new Error(`WebSocket connection failed: ${err.message}`));
        });
      });
    } catch (err) {
      this.status.set('error');
      throw err;
    }

    // Forward file change events from sandbox agent to SandboxFileSystem watch callbacks.
    // Server batches events (up to 50 / 100 ms) to avoid flooding on pod-boot initial scans.
    this._socket.on('files:changed-batch', (events: any[]) => {
      for (const event of events) {
        this.fs.handleWatchEvent(event);
      }
    });

    // Seed the file tree from the workspace listing.
    //
    // The socket only carries what the pod's agent log still holds, and
    // supervisord truncates that log at 1 MB with no backups — the agent's
    // initial scan emits every file WITH content and blows the cap in seconds.
    // Replaying it after a refresh returned a handful of events for a 24k-file
    // workspace, so the Code tab came back with one file in it. The listing is
    // the authoritative picture; the socket keeps carrying live deltas on top.
    //
    // Deliberately not awaited: the tree filling in a moment later is fine, and
    // a slow or sleeping pod must not hold up the boot.
    void this._hydrateFileTree(chatId);

    /*
     * Build-queue events. They ride this socket rather than opening their own
     * because the chat room is already joined and already authenticated — a
     * queued user needs no extra connection, and the position updates arrive on
     * the same channel that is proving the session is alive.
     *
     * The store also polls as a fallback, so a socket that never connects
     * degrades to a slower update rather than a frozen counter.
     */
    this._socket.on(
      'sandbox:queue-update',
      (ticket: { chatId: string; position: number; total: number; etaSec: number }) => {
        applyQueueUpdate(ticket);
      },
    );

    this._socket.on('sandbox:queue-admitted', (event: { chatId: string }) => {
      admitFromQueue(event.chatId);
    });

    // Only treat the frontend dev server port as previewable
    const PREVIEW_PORT = 5173;

    // Listen for port events
    this._socket.on('ports:changed', (event: { type: string; port: number }) => {
      if (event.port !== PREVIEW_PORT) {
        return;
      }

      if (event.type === 'port:open') {
        this._knownPorts.add(event.port);

        // Get the direct port-forward URL from the server
        const portChatId = this._chatId;
        this._authedFetch(`/api/sandbox/${this._chatId}/preview-url`)
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            const previewUrl = data?.url || `/p/${portChatId}/`;
            console.log(`[Sandbox] Preview ready — ${previewUrl}`);

            // The dev server bound :5173 and its preview URL resolved — this is
            // the "actually serving" signal, so it's the ONLY honest moment to
            // declare 'running' (DEV-155). Promote from 'starting' only, so a
            // late port event can't stomp a 'sleeping'/'error'/switched-chat state.
            this._markServing(portChatId);

            for (const cb of this._serverReadyCallbacks) {
              cb(event.port, previewUrl);
            }

            for (const cb of this._portCallbacks) {
              cb(event.port, 'open', previewUrl);
            }
          })
          .catch(() => {});
      } else if (event.type === 'port:close') {
        this._knownPorts.delete(event.port);

        for (const cb of this._portCallbacks) {
          cb(event.port, 'close', '');
        }
      }
    });

    // Listen for session timeout warnings (15 min before disconnect)
    this._socket.on(
      'sandbox:connection-warning',
      (data: { chatId: string; remainingMs: number; disconnectAt: string; message: string }) => {
        console.log(`[Sandbox] Session warning: ${data.message}`);
        setTimeoutWarning(data);
      },
    );

    // Listen for session timeout (force disconnect)
    this._socket.on('sandbox:connection-timeout', (data: { chatId: string; reason: string }) => {
      console.log(`[Sandbox] Session timed out: ${data.reason}`);
      setTimedOut(data.reason);
      this.status.set('sleeping');
      this._booted = false;
    });

    // The server confirmed our pod is gone (idle-GC'd while we were away) and
    // couldn't attach its agent listener. Reset local state and re-run boot()
    // ourselves — that's the only path that recreates the pod AND restores the
    // project from its GitHub snapshot. Without this, boot()'s own "already
    // booted" guard sees this socket as still connected (destroyPod never
    // touches it) and never re-provisions, so every future 'join' 404s forever
    // (see lessons/2026-08-07-dunea-ai-bad-session.md #1).
    //
    // Rebooting only makes sense if someone is actually looking. A background
    // tab that reconnects hours later provisions a fresh pod for nobody, which
    // then idles past IDLE_TIMEOUT_MS and gets reclaimed, which fires
    // pod-not-found again — chat dcc8c2ba cycled create/GC four times over 21
    // hours with its owner long gone. On a pool capped by the DO droplet limit
    // those are pods a real signup can't claim, so park the reboot on a hidden
    // tab and run it the moment it's foregrounded.
    this._socket.on('sandbox:pod-not-found', (data: { chatId: string }) => {
      this.status.set('sleeping');
      this._booted = false;

      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        console.log(`[Sandbox] Pod not found for chat ${data.chatId} — deferring reboot until the tab is visible`);
        this._deferReboot(data.chatId);

        return;
      }

      console.log(`[Sandbox] Pod not found for chat ${data.chatId} — recreating`);
      this._bootInBackground(data.chatId, 'sandbox_pod_not_found_reboot');
    });

    // Clear timeout state on successful boot
    clearTimeoutState();

    // Socket joined — but the dev server is NOT necessarily serving yet, so we
    // land in 'starting', not 'running' (DEV-155). 'running' is declared only
    // once serving is confirmed: a :5173 port event (fresh boot / restart), or
    // the serving-probe below (a resumed pod whose server is already up, which
    // won't re-emit a port transition on reconnect).
    this._booted = true;

    if (this.status.get() === 'booting') {
      this.status.set('starting');
    }

    console.log(`[Sandbox] Socket joined — chatId=${chatId}, status=starting`);

    // NOTE: we deliberately do NOT probe /preview-url here to reach 'running'.
    // That endpoint runs ensurePreviewReady (it can *start/recover* a dev
    // server), so probing a fresh chat — whose project the agent hasn't
    // scaffolded yet — would kick off a premature/conflicting dev-server start.
    // Instead 'running' is driven purely by the :5173 port event: for a fresh
    // boot it fires when the agent starts the server; on reconnect to a live pod
    // the gateway replays the historical port:open from the agent log
    // (`tail -f -n +1`), so those flows reach 'running' too. The resume paths in
    // useChatHistory also promote explicitly once their preview-url fetch
    // succeeds (belt-and-suspenders).
  }

  /**
   * Promote 'starting' → 'running'. Only from 'starting' and only if we're still
   * on this chat, so a late/stale/replayed serving signal can't stomp a
   * 'sleeping', 'error', or switched-chat state (DEV-155).
   */
  private _markServing(chatId: string | null): void {
    if (this._chatId === chatId && this.status.get() === 'starting') {
      this.status.set('running');
      console.log(`[Sandbox] Serving confirmed — chatId=${chatId}, status=running`);
    }
  }

  /**
   * Spawn a process in the sandbox pod.
   * Returns a SandboxProcess with stdin/stdout streams backed by the socket connection.
   */
  async spawn(command: string, args: string[] = [], _options?: any): Promise<SandboxProcess> {
    if (!this._socket || !this._chatId) {
      throw new Error('Sandbox not booted. Call boot() first.');
    }

    const fullCommand = [command, ...args];
    const process = new SandboxProcess(this._socket, this._chatId, fullCommand);
    await process.start();

    return process;
  }

  on(event: 'server-ready', callback: ServerReadyCallback): void;
  on(event: 'port', callback: PortCallback): void;
  on(event: 'preview-message', callback: PreviewMessageCallback): void;
  on(event: string, callback: any): void {
    switch (event) {
      case 'server-ready':
        this._serverReadyCallbacks.push(callback);
        break;
      case 'port':
        this._portCallbacks.push(callback);
        break;
      case 'preview-message':
        this._previewMessageCallbacks.push(callback);
        break;
    }
  }

  getPreviewUrl(_port: number = 5173): string {
    return `/p/${this._chatId}/`;
  }

  async teardown(): Promise<void> {
    if (this._chatId) {
      try {
        await this._authedFetch(`/api/sandbox/${this._chatId}`, { method: 'DELETE' });
      } catch {
        // Best effort
      }
    }

    if (this._socket) {
      this._socket.disconnect();
      this._socket = null;
    }

    this._booted = false;
    this._chatId = null;
    this._knownPorts.clear();
    this._serverReadyCallbacks = [];
    this._portCallbacks = [];
    this._previewMessageCallbacks = [];

    // The runtime is a module singleton that outlives any one chat (and HMR),
    // so a parked reboot left behind here would keep a document listener alive
    // for a chat that has just been torn down.
    this._clearDeferredReboot();
  }

  getSocket(): Socket | null {
    return this._socket;
  }

  getChatId(): string | null {
    return this._chatId;
  }

  isBooted(): boolean {
    return this._booted;
  }

  /**
   * Fill the file tree from `GET /api/sandbox/:chatId/tree`.
   *
   * Delivered through the same watch-event pipe the socket uses rather than by
   * calling the file store directly: the store lives under `workbenchStore`,
   * which imports this runtime, so reaching back into it here would close an
   * import cycle. The store treats seed events as additive — it never
   * overwrites a path it already knows — so a late listing cannot blank a file
   * the user is looking at.
   *
   * Failure is silent: the tree stays as whatever the socket provided, which is
   * exactly the old behaviour.
   */
  private async _hydrateFileTree(chatId: string): Promise<void> {
    try {
      const entries = await apiGetSandboxTree(chatId);

      this.fs.handleWatchEvents(
        entries.map((entry) => ({
          type: entry.type === 'folder' ? 'tree:seed_dir' : 'tree:seed_file',
          path: entry.path,
        })),
      );

      console.log(`[Sandbox] file tree seeded from listing: ${entries.length} entries`);
    } catch (err: any) {
      console.warn(`[Sandbox] file tree listing failed, falling back to socket events: ${err?.message}`);
    }
  }
}
